/**
 * Reminders.jsx
 * src/pages/assistant/Reminders.jsx
 */

import { useState, useEffect } from "react";
import { BSModal, DeleteModal, Avatar, PageHeader, Loading, EmptyState, MiniStat, apiFetch } from "../../components/helpers";

const TYPES   = ["Appointment","Follow-up","Medication","Payment","General"];
const CHANNELS = ["SMS","WhatsApp","Email","In-App"];
const STATUSES = ["Pending","Sent","Failed","Cancelled"];
const EMPTY_FORM = { reminder_id: null, patient_name: "", patient_mobile: "", reminder_type: "Appointment", channel: "SMS", message: "", scheduled_at: "", status: "Pending" };


export default function Reminders() {
  const [items,      setItems]      = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [search,     setSearch]     = useState("");
  const [filter,     setFilter]     = useState("All");
  const [modalOpen,  setModalOpen]  = useState(false);
  const [deleteId,   setDeleteId]   = useState(null);
  const [form,       setForm]       = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [error,      setError]      = useState("");
  const [flash,      setFlash]      = useState({ msg: "", type: "success" });

  const load = async () => {
    setLoading(true);
    try {
      const json = await apiFetch("get_reminders.php");
      if (json.status) setItems(json.data || []);
    } catch { setItems([]); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);
  const showFlash = (msg, type = "success") => { setFlash({ msg, type }); setTimeout(() => setFlash({ msg: "", type: "success" }), 3000); };
  const openAdd  = () => { setForm({ ...EMPTY_FORM, scheduled_at: new Date().toISOString().slice(0, 16) }); setError(""); setModalOpen(true); };
  const openEdit = (r) => { setForm({ ...r, scheduled_at: r.scheduled_at.replace(" ", "T") }); setError(""); setModalOpen(true); };

  const handleSubmit = async () => {
    if (!form.patient_name.trim())   return setError("Patient name is required");
    if (!form.patient_mobile.trim()) return setError("Patient mobile is required");
    if (!form.message.trim())        return setError("Message is required");
    if (!form.scheduled_at)          return setError("Schedule date/time is required");
    setSubmitting(true); setError("");
    try {
      const isEdit = !!form.reminder_id;
      const endpoint = isEdit ? "update_reminder.php" : "add_reminder.php";
      const json = await apiFetch(endpoint, "POST", form);
      if (json.status) { setModalOpen(false); showFlash(json.message || "Reminder saved!"); load(); }
      else setError(json.message || "Something went wrong");
    } catch { setError("Network error"); }
    setSubmitting(false);
  };

  const handleSendNow = async (id) => {
    try {
      const json = await apiFetch("send_reminder_now.php", "POST", { reminder_id: id });
      if (json.status) { showFlash("Reminder sent!"); load(); }
      else showFlash(json.message || "Failed to send", "danger");
    } catch { showFlash("Network error", "danger"); }
  };

  const handleDelete = async () => {
    try {
      const json = await apiFetch("delete_reminder.php", "POST", { reminder_id: deleteId });
      if (json.status) { setDeleteId(null); showFlash("Reminder deleted!"); load(); }
    } catch {}
  };

  const filtered = items.filter(r =>
    (filter === "All" || r.status === filter) &&
    (r.patient_name?.toLowerCase().includes(search.toLowerCase()) ||
     r.reminder_type?.toLowerCase().includes(search.toLowerCase()))
  );

  const pending = items.filter(i => i.status === "Pending").length;
  const sent    = items.filter(i => i.status === "Sent").length;
  const failed  = items.filter(i => i.status === "Failed").length;

  const CHANNEL_ICON = { SMS: "📱", WhatsApp: "💚", Email: "📧", "In-App": "🔔" };
  const STATUS_BADGE = { Pending: "text-bg-warning", Sent: "text-bg-success", Failed: "text-bg-danger", Cancelled: "text-bg-secondary" };
  const inp = "form-control";

  return (
    <div className="p-3 p-lg-4" style={{ minHeight: "100vh", fontFamily: "'Nunito','Segoe UI',sans-serif" }}>
      <PageHeader title="Send Reminders" subtitle={`${filtered.length} reminder${filtered.length !== 1 ? "s" : ""}`}>
        <select className="form-select" value={filter} onChange={e => setFilter(e.target.value)} style={{ width: 150, fontSize: 13 }}>
          <option value="All">All Status</option>
          {STATUSES.map(s => <option key={s}>{s}</option>)}
        </select>
        <input className={inp} placeholder="Search patient or type..." value={search} onChange={e => setSearch(e.target.value)} style={{ width: 220, fontSize: 13 }} />
        <button className="btn fw-bold text-white px-4" style={{ background: "#D85A30", borderRadius: 10 }} onClick={openAdd}>+ New Reminder</button>
      </PageHeader>

      <div className="row g-3 mb-4">
        <div className="col-6 col-md-3"><MiniStat label="Total" value={items.length} accent="#D85A30" /></div>
        <div className="col-6 col-md-3"><MiniStat label="Pending" value={pending} accent="#BA7517" /></div>
        <div className="col-6 col-md-3"><MiniStat label="Sent" value={sent} accent="#1D9E75" /></div>
        <div className="col-6 col-md-3"><MiniStat label="Failed" value={failed} accent="#E24B4A" /></div>
      </div>

      {flash.msg && <div className={`alert alert-${flash.type} py-2 px-3 mb-3`} style={{ fontSize: 13 }}>{flash.msg}</div>}

      <div className="card border-0 shadow-sm" style={{ borderRadius: 16 }}>
        <div className="card-body p-0">
          {loading ? <Loading /> : filtered.length === 0 ? <EmptyState message="No reminders found." /> : (
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0">
                <thead style={{ background: "#FAFBFC" }}>
                  <tr>{["Patient","Type","Channel","Scheduled","Message","Status","Actions"].map(h => <th key={h} className="text-muted fw-bold border-0 px-4 py-3" style={{ fontSize: 10, letterSpacing: ".6px", textTransform: "uppercase" }}>{h}</th>)}</tr>
                </thead>
                <tbody>
                  {filtered.map((r, i) => (
                    <tr key={i}>
                      <td className="px-4">
                        <div className="d-flex align-items-center gap-2">
                          <Avatar name={r.patient_name} size={28} />
                          <div>
                            <div className="fw-semibold" style={{ fontSize: 13 }}>{r.patient_name}</div>
                            <div className="text-muted" style={{ fontSize: 11, fontFamily: "monospace" }}>{r.patient_mobile}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4"><span className="badge text-bg-info" style={{ fontSize: 11 }}>{r.reminder_type}</span></td>
                      <td className="px-4 text-muted" style={{ fontSize: 13 }}>{CHANNEL_ICON[r.channel]} {r.channel}</td>
                      <td className="px-4 text-muted" style={{ fontSize: 12 }}>{r.scheduled_at}</td>
                      <td className="px-4 text-muted text-truncate" style={{ fontSize: 12, maxWidth: 180 }}>{r.message}</td>
                      <td className="px-4"><span className={`badge ${STATUS_BADGE[r.status] || "text-bg-secondary"}`} style={{ fontSize: 11 }}>{r.status}</span></td>
                      <td className="px-4">
                        <div className="d-flex gap-2 flex-wrap">
                          {r.status === "Pending" && <button className="btn btn-sm fw-bold" style={{ background: "#E1F5EE", color: "#085041", fontSize: 11 }} onClick={() => handleSendNow(r.reminder_id)}>Send Now</button>}
                          <button className="btn btn-sm fw-bold" style={{ background: "#E6F1FB", color: "#042C53", fontSize: 11 }} onClick={() => openEdit(r)}>Edit</button>
                          <button className="btn btn-sm fw-bold" style={{ background: "#FCEBEB", color: "#501313", fontSize: 11 }} onClick={() => setDeleteId(r.reminder_id)}>Delete</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <BSModal open={modalOpen} title={form.reminder_id ? "Edit Reminder" : "New Reminder"} size="modal-lg" onClose={() => setModalOpen(false)}>
        <div className="row g-3">
          <div className="col-md-6"><label className="form-label fw-bold text-muted" style={{ fontSize: 11 }}>PATIENT NAME *</label><input className={inp} value={form.patient_name} onChange={e => setForm({ ...form, patient_name: e.target.value })} placeholder="e.g. Priya Sharma" /></div>
          <div className="col-md-6"><label className="form-label fw-bold text-muted" style={{ fontSize: 11 }}>PATIENT MOBILE *</label><input className={inp} value={form.patient_mobile} onChange={e => setForm({ ...form, patient_mobile: e.target.value })} placeholder="+91 98765 43210" /></div>
          <div className="col-md-4"><label className="form-label fw-bold text-muted" style={{ fontSize: 11 }}>TYPE</label><select className="form-select" value={form.reminder_type} onChange={e => setForm({ ...form, reminder_type: e.target.value })}>{TYPES.map(t => <option key={t}>{t}</option>)}</select></div>
          <div className="col-md-4"><label className="form-label fw-bold text-muted" style={{ fontSize: 11 }}>CHANNEL</label><select className="form-select" value={form.channel} onChange={e => setForm({ ...form, channel: e.target.value })}>{CHANNELS.map(c => <option key={c}>{c}</option>)}</select></div>
          <div className="col-md-4"><label className="form-label fw-bold text-muted" style={{ fontSize: 11 }}>SCHEDULE DATE/TIME *</label><input type="datetime-local" className={inp} value={form.scheduled_at} onChange={e => setForm({ ...form, scheduled_at: e.target.value })} /></div>
          <div className="col-12">
            <label className="form-label fw-bold text-muted" style={{ fontSize: 11 }}>MESSAGE *</label>
            <textarea className={inp} rows={3} value={form.message} onChange={e => setForm({ ...form, message: e.target.value })} placeholder="e.g. Dear [Patient], your appointment is scheduled for tomorrow at 10:00 AM. Please be on time." />
            <div className="form-text">Use [Patient], [Doctor], [Date], [Time] as dynamic placeholders.</div>
          </div>
        </div>
        {error && <div className="alert alert-danger py-2 px-3 mt-3 mb-0" style={{ fontSize: 12 }}>{error}</div>}
        <div className="d-flex gap-2 justify-content-end mt-3">
          <button className="btn btn-light fw-semibold" onClick={() => setModalOpen(false)}>Cancel</button>
          <button className="btn fw-bold text-white px-4" style={{ background: "#D85A30" }} onClick={handleSubmit} disabled={submitting}>
            {submitting ? <><span className="spinner-border spinner-border-sm me-1" />Saving...</> : "Save Reminder"}
          </button>
        </div>
      </BSModal>

      <DeleteModal open={!!deleteId} onClose={() => setDeleteId(null)} onConfirm={handleDelete} />
    </div>
  );
}